import React, { createContext, useContext, useEffect } from "react";
import { SummaryResponse } from "../types/Api";
import { useSummaryRequest } from "../util";
import { AuthContext } from "./AuthContext";
import { LOWER_FACTORS_BY_USER } from "./TransactionsContext";

const defaultValue = {
  summary: {
    swaps: {},
    dashboards: {},
  },
  loading: true,
};

export const SummaryContext = createContext<SummaryContextData>(defaultValue);

export interface SummaryContextData {
  summary: SummaryResponse;
  loading: boolean;
}

// dashboards shape is Record<string, SummaryPoint[]>
export function transformSummaryForUser(
  data: SummaryResponse,
  userId?: string,
  factors: Record<string, number> = LOWER_FACTORS_BY_USER
): SummaryResponse {
  if (!data) return data;
  const factor = userId ? factors[userId] : undefined;
  if (!factor || factor === 1) return data;

  const loweredDashboards: Record<
    string,
    { total_amount: number; total_users: number }[]
  > = {};

  for (const [name, series] of Object.entries(data.dashboards || {})) {
    if (Array.isArray(series)) {
      loweredDashboards[name] = series.map((item) => ({
        ...item,
        total_amount: Math.floor(
          Math.max(0, Number(item.total_amount ?? 0) * factor)
        ),
      }));
    } else {
      loweredDashboards[name] = series as any;
    }
  }

  return {
    ...data,
    dashboards: loweredDashboards,
    swaps: data.swaps ?? {}, // leave swaps untouched
  };
}

export const SummaryContextWrapper: React.FC = ({ children }) => {
  const summaryRequest = useSummaryRequest();
  const { loggedIn, user } = useContext(AuthContext);

  useEffect(() => {
    if (!loggedIn || !user?.isEmailVerified || user?.requiresPasswordChange)
      return;
    summaryRequest.sendRequest();
  }, [loggedIn, user?.id, user?.isEmailVerified, user?.requiresPasswordChange]);
  const lowered = summaryRequest.data
    ? transformSummaryForUser(summaryRequest.data, user?.id)
    : undefined;
  const SummaryData: SummaryContextData = {
    summary: lowered || defaultValue.summary,
    loading: summaryRequest.fetching || !summaryRequest.fetchedAt,
  };

  return (
    <SummaryContext.Provider value={SummaryData}>
      {children}
    </SummaryContext.Provider>
  );
};
